//Build-in libraries

import { CheckIcon, XCircleIcon } from '@heroicons/react/24/outline';

//Third party libraries
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
//Assets and Miscellaneous
import { useDispatch } from 'react-redux';
import { type ClientRole, getAllRoles } from '@/api/roles-and-permission';
import { patchTitle } from '@/api/Terminal/OrgTitle';
//Services
import type { PatchTitlePayload } from '@/api/Terminal/OrgTitle/types';
//Custom Components
import ReactSelect from '@/components/UI/Form/ReactSelect';
import EditIcon from '@/components/UI/Icons/EditIcon';
import LoadingRolesAndTitles from '@/components/UI/loading/RolesAndTitles';
import Table from '@/components/UI/Table';
import { ReactQueryKey } from '@/constants/ReactQueryKeys';
import { useOrgTitles } from '@/hooks/ReactQueryHooks/useTitlesQuery';
import { notifyUser } from '@/store/Toast';
import type { OrgTitlesInterface } from '@/types/role';
import { sanitizeResponseError } from '@/utils';
import EditableCell from './EditableCell';

export default function OrgTitlesTable() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [data, setData] = useState<OrgTitlesInterface[]>([]);
  const [_skipPageReset, setSkipPageReset] = useState(false);
  const [perPage, setPerPage] = useState<number>(10);
  const [page, setPage] = useState<number>(1);

  //Tanstack: version 5 query implementation
  const fetchRoles = useQuery({ queryKey: [ReactQueryKey.GET_ROLES], queryFn: () => getAllRoles() });
  const clientRoles = fetchRoles?.data?.data;

  const updateOrgTitles = useMutation((data: { titleId: number; data: PatchTitlePayload }) => patchTitle(data), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ReactQueryKey.GET_ORG_TITLES] });
    },
    onError: (error: any) => {
      cancelUpdate();
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  const fetchOrgTitles = useOrgTitles({ params: { perPage, page } });
  const orgTitles = fetchOrgTitles?.data?.data;
  const orgTitlesCount = fetchOrgTitles?.data?.meta?.total || 0;
  const pageCount = Math.ceil(orgTitlesCount / perPage);

  useEffect(() => {
    setData(orgTitles || []);
  }, [orgTitles]);

  const saveTitle = (title: OrgTitlesInterface) => {
    const updateTitle: PatchTitlePayload = {
      name: title.name.trim(),
      roleId: title.role.id,
    };
    updateOrgTitles.mutate({ titleId: title.id, data: updateTitle });
  };

  const updateTableData = (rowIndex: number, columnId: string, value: string | { name: string; id: string }) => {
    // We also turn on the flag to not reset the page
    setSkipPageReset(true);
    setData((prevState) =>
      prevState.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...prevState[rowIndex],
            [columnId]: value,
          };
        }
        return row;
      })
    );
  };

  const cancelUpdate = () => {
    queryClient.invalidateQueries({ queryKey: [ReactQueryKey.GET_ORG_TITLES] });
    setData(orgTitles || []);
  };

  const tableColumns: ColumnDef<any>[] = [
    {
      id: 'name',
      header: 'Title',
      accessorKey: 'name',
    },
    {
      id: 'role',
      header: 'Role',
      accessorKey: 'role',
      cell: (props) => {
        const { row, getValue, table, column } = props;
        const roleValue = getValue() as { name: string; id: string };
        const title = row.original;
        const meta = table.options.meta as any;
        const editableRowIndex = meta?.editableRowIndex;

        if (editableRowIndex !== row.index) {
          return <div>{roleValue?.name}</div>;
        } else {
          return (
            <ReactSelect<ClientRole>
              id='roles'
              name='roles'
              defaultValue={{
                name: title.role.name,
                slug: title.role.id,
              }}
              options={clientRoles}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.slug}
              onChange={(option) => {
                if (option) {
                  const selectedOption = {
                    name: option.name,
                    id: option.slug,
                  };
                  updateTableData(row.index, column.id, selectedOption);
                }
              }}
            />
          );
        }
      },
    },
    {
      id: 'actions',
      header: '',
      accessorKey: 'actions',
      size: 36,
      cell: (props) => {
        const { row, table } = props;
        const title = row.original;
        const meta = table.options.meta as any;
        const editableRowIndex = meta?.editableRowIndex;
        const setEditableRowIndex = meta?.setEditableRowIndex;

        if (editableRowIndex !== row.index) {
          return (
            <div
              className='flex justify-center'
              onClick={() => {
                cancelUpdate();
                fetchRoles.refetch();
                setEditableRowIndex?.(row.index);
              }}
            >
              <EditIcon />
            </div>
          );
        } else {
          return (
            <div className='flex justify-center gap-2'>
              <CheckIcon
                className='h-6 w-6 text-primaryColor-normal bg-neutralColor-lightest hover:bg-neutralColor-light rounded-full p-1 transition duration-300 ease-in-out'
                onClick={() => {
                  setEditableRowIndex?.(null);
                  saveTitle(title);
                }}
              />
              <XCircleIcon
                className='h-6 w-6 text-tertiaryColor-normal bg-neutralColor-lightest hover:bg-neutralColor-light rounded-full p-1 transition duration-300 ease-in-out'
                onClick={() => {
                  setEditableRowIndex?.(null);
                  cancelUpdate();
                }}
              />
            </div>
          );
        }
      },
    },
  ];

  const defaultColumn: Partial<ColumnDef<any>> = {
    cell: EditableCell,
  };

  const nextPageHandler = () => {
    setPage((prevState) => (prevState === pageCount ? pageCount : page + 1));
  };

  const previousPageHandler = () => {
    setPage((prevState) => (prevState > 1 ? page - 1 : 1));
  };

  const tablePagination = {
    page: page,
    pageSize: perPage,
    pageCount,
    currentPage: page,
    setPageSize: (v: number) => setPerPage(v),
    nextPage: nextPageHandler,
    previousPage: previousPageHandler,
  };

  return (
    <>
      {fetchOrgTitles.isLoading && <LoadingRolesAndTitles />}
      {data && !fetchOrgTitles.isLoading && (
        <Table
          cols={tableColumns}
          defaultColumn={defaultColumn}
          tableData={data}
          updateTableData={updateTableData}
          pagination={tablePagination}
        />
      )}
    </>
  );
}
